// Slides route — renders the whiteboard shell in slides mode.
// Downstream whiteboard components now derive slides mode from the route name
// (SLIDES_VIEW) rather than from a ?slides=true query param, so no query-param
// bridge is needed.

import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { CONTAINER_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { isEmbeddedWhiteboardMode } from '@confluence/whiteboard-common/entry-points/isEmbeddedWhiteboardMode.ts';
import { EmbeddedWhiteboardRouteWrapper } from '@confluence/whiteboard/entry-points/EmbeddedWhiteboardRouteWrapper.tsx';

import { WhiteboardRouteComponentLoader } from './WhiteboardRouteComponentLoader.tsx';

const isEmbeddedWhiteboard = ({ query }: RouteMatch) =>
	isEmbeddedWhiteboardMode({
		parentProduct: query.parentProduct,
		presentationMode: query.presentationMode,
	});

export const SlidesRoute: {
	(match: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS(routeMatch: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					contentId: string;
					focusedCommentId: string | string[] | undefined;
					focusedMentionId: string | string[] | undefined;
				};
			};
			pageState: {
				contentId: string;
				spaceKey: string;
				contentType: string;
				routeName: string;
			};
		};
		MainLayout:
			| boolean
			| {
					navView: string;
					spaceKey: string;
					isViewPage: boolean;
			  };
	};
} = (match: RouteMatch): React.JSX.Element => {
	const {
		params: { contentId, spaceKey },
	} = match;
	const isEmbeddedMode = isEmbeddedWhiteboard(match);

	const element = (
		<WhiteboardRouteComponentLoader
			spaceKey={spaceKey}
			contentId={contentId}
			isEmbeddedWhiteboard={isEmbeddedMode}
		/>
	);

	if (isEmbeddedMode) {
		return <EmbeddedWhiteboardRouteWrapper>{element}</EmbeddedWhiteboardRouteWrapper>;
	}

	return element;
};

SlidesRoute.NAVIGATION_PARAMS = ({
	name: routeName,
	params: { contentId, spaceKey },
	query: { focusedCommentId, focusedMentionId, parentProduct, presentationMode },
}: RouteMatch): {
	Screen: {
		screenEvent: {
			name: string;
			id: string;
			attributes: {
				contentId: string;
				focusedCommentId: string | string[] | undefined;
				focusedMentionId: string | string[] | undefined;
			};
		};
		pageState: {
			contentId: string;
			spaceKey: string;
			contentType: string;
			routeName: string;
		};
	};
	MainLayout:
		| boolean
		| {
				navView: string;
				spaceKey: string;
				isViewPage: boolean;
		  };
} => ({
	Screen: {
		screenEvent: {
			name: 'slideScreen',
			id: contentId,
			attributes: {
				contentId,
				focusedCommentId,
				focusedMentionId,
			},
		},
		pageState: {
			contentId,
			spaceKey,
			contentType: 'slide',
			routeName,
		},
	},
	MainLayout: isEmbeddedWhiteboardMode({ parentProduct, presentationMode })
		? false
		: { navView: CONTAINER_HOME_ID, spaceKey, isViewPage: true },
});
